import { z } from "zod";
/** Table settings the host controls. Public and ranked tables fix these server-side. */
export const TableSettings = z.object({
    playerCount: z.number().int().min(2).max(4).nullable(),
    turnSeconds: z.number().int().min(1).max(60),
    minimum50: z.boolean(),
    oneCardAlert: z.boolean(),
    minus100: z.boolean(),
    endBy: z.enum(["rounds", "target"]),
    rounds: z.number().int().min(1).max(20),
    targetScore: z.number().int().min(100).max(10000),
    seatOrder: z.enum(["fixed", "shuffled"]),
    special1000: z.literal(true).default(true),
    competitive: z.boolean().default(false),
    /** D-120: friends of anyone seated may watch this private table (the host's switch; absent means on). */
    friendsCanWatch: z.boolean().optional(),
});
export const DEFAULT_TABLE_SETTINGS = {
    playerCount: 4, turnSeconds: 30, minimum50: true, oneCardAlert: true, minus100: true,
    endBy: "rounds", rounds: 10, targetScore: 1000, seatOrder: "shuffled", special1000: true, competitive: false,
};
export const RoomKind = z.enum(["private", "practice", "online", "ranked", "club", "tournament"]);
export const requestId = z.string().regex(/^[-\w]{8,80}$/);
export const SeriesSettings = z.object({
    mode: z.enum(["open", "games", "target"]),
    games: z.number().int().min(1).max(20).default(5),
    targetScore: z.number().int().min(100).max(10000).default(2000),
});
/** Every table action. All are HTTP POST /rooms/:code/act and idempotent per (player, requestId). */
export const RoomAction = z.discriminatedUnion("type", [
    z.object({ type: z.literal("ready"), requestId, version: z.number().int(), ready: z.boolean() }),
    z.object({ type: z.literal("start"), requestId, version: z.number().int() }),
    z.object({ type: z.literal("draw"), requestId, version: z.number().int(), source: z.enum(["stock", "discard"]) }),
    z.object({ type: z.literal("discard"), requestId, version: z.number().int(), card: z.number().int().min(0).max(51), declare: z.boolean() }),
    z.object({ type: z.literal("timeBank"), requestId, version: z.number().int() }),
    z.object({ type: z.literal("resume"), requestId, version: z.number().int() }),
    z.object({ type: z.literal("leave"), requestId, version: z.number().int() }),
    z.object({ type: z.literal("rematch"), requestId, version: z.number().int() }),
    z.object({ type: z.literal("online-start"), requestId, version: z.number().int() }),
    z.object({ type: z.literal("find-more"), requestId, version: z.number().int() }),
    z.object({ type: z.literal("settings"), requestId, version: z.number().int(), patch: TableSettings.partial() }),
    z.object({ type: z.literal("series-config"), requestId, version: z.number().int(), series: SeriesSettings }),
    z.object({ type: z.literal("series-new"), requestId, version: z.number().int(), confirm: z.literal(true) }),
    z.object({ type: z.literal("series-casual"), requestId, version: z.number().int(), confirm: z.literal(true) }),
    /** Club tables with two or three seated, all ready: each player's "Start with N players" (a toggle, D-079). */
    z.object({ type: z.literal("club-start"), requestId, version: z.number().int() }),
    /** The host removes a player from a private or club table's waiting room (`player` is a list position, D-079). */
    z.object({ type: z.literal("remove"), requestId, version: z.number().int(), player: z.number().int().min(0).max(3) }),
    z.object({ type: z.literal("club-rules"), requestId, version: z.number().int(), rules: TableSettings.pick({ minimum50: true, oneCardAlert: true, turnSeconds: true, minus100: true }).partial() }),
]);
/** Computer opponent strength at a practice table, 1 (Shallows) to 4 (Open sea). D-049, D-050. */
export const BotLevel = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
/** Player-facing names: a depth scale, so they never clash with player rating tiers. */
export const BOT_LEVEL_NAMES = { 1: "Shallows", 2: "Reef", 3: "Channel", 4: "Open sea" };
export const CreateRoom = z.object({
    kind: z.enum(["private", "practice"]),
    opponents: z.number().int().min(1).max(3).optional(),
    /** Practice only; the server uses Channel when it is missing. */
    botLevel: BotLevel.optional(),
    settings: TableSettings.partial().optional(),
    /** D-192: practice only; ready the player and deal the first round at once (no waiting room). */
    deal: z.boolean().optional(),
    requestId,
});
export const QUICK_PHRASES = ["Good luck", "Nice play", "Well played", "One moment", "Thanks", "Good game"];
export const REACTIONS = {
    free: ["nice", "hi", "you", "bravo", "love", "patience"],
    reef: ["snapper", "crab", "tuna", "octopus", "porcupinefish", "manta"],
    /**
     * Throwables (D-093): they fly to the target's picture and play their own clip there. Since D-097 `throws` is the
     * free set called Basic and `island` is the Vaancho pack (shop item `pack-island`); the ids stay so owners keep it.
     */
    throws: ["poop", "kiss", "energy", "rocket", "rose", "coffee", "gadha", "bajiya"],
    island: ["fishslap", "egg", "slipper", "juicepetty", "tomato", "bomb", "watermelon", "bulb", "broom"],
    /** D-195: reaction packs sold again as new themes (shop items `pack-<key>`); they pop over the sender like the hands. */
    masdhoni: ["catch", "hook", "strike", "noddy", "dhoni", "net"],
    saifihaara: ["sai", "kettle", "masroshi", "gulha", "thinking", "fistbump"],
    weather: ["monsoon", "lightning", "rainbow", "sunrise", "calmsea", "waterspout"],
    boduberu: ["beru", "clapalong", "dance", "bonfire", "firework", "palms"],
    /** D-195: the Sai break throw pack (`pack-saibreak`). */
    saibreak: ["saisplash", "gulhatoss", "bondibai", "kurumba", "mundu"],
    /**
     * D-202: packs after cardsclassic's (same owner, art redrawn in the D-152 style). Island life's sunrise, monsoon and
     * catch take an `isle` prefix (Weather moods and Masdhoni have those ids); the packs called Cheers and Playful stay retired.
     */
    cardroom: ["shuffle", "aces", "crown", "sweat", "micdrop", "allin", "pokerface"],
    islandlife: ["islesunrise", "leap", "chill", "islemonsoon", "islecatch", "sailaway", "crabdance"],
    party: ["cake", "confetti", "balloon"],
    spicy: ["chili", "pie"],
    sweethearts: ["heart", "snowball"],
    beach: ["flipflop", "sandbucket", "starfish", "jellyfish", "beachball", "seaweed", "squid"],
    cardsharp: ["spincard", "chipstack", "rubberchicken", "dice", "teacup", "deck", "joker"],
};
/** Packs whose reactions are thrown at a player's picture instead of shown as a tile. */
export const THROW_PACKS = ["throws", "island", "saibreak", "party", "spicy", "sweethearts", "beach", "cardsharp"];
/** D-195: reaction packs sold in the Shop (item `pack-<key>`), in the order the React sheet shows them. */
export const SOLD_REACTION_PACKS = ["masdhoni", "saifihaara", "weather", "boduberu", "cardroom", "islandlife"];
/** Throw packs sold in the Shop, after the free Basic set. */
export const SOLD_THROW_PACKS = ["island", "saibreak", "party", "spicy", "sweethearts", "beach", "cardsharp"];
export const isThrow = (pack) => THROW_PACKS.includes(pack);
//# sourceMappingURL=room.js.map